import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Leaf, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../lib/api';
import { toast } from 'sonner';

export const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        if (password.length < 6) {
            toast.error('Le mot de passe doit faire au moins 6 caractères');
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, new_password: password.normalize('NFC').trim() });
            setDone(true);
            toast.success('Mot de passe modifié !');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Lien invalide ou expiré');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">Lien invalide.</p>
                        <Link to="/connexion"><Button className="mt-4">Retour à la connexion</Button></Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand flex items-center justify-center p-4" data-testid="reset-password-page">
            <Card className="w-full max-w-md border-0 shadow-xl bg-white/80 backdrop-blur-xl rounded-2xl">
                <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-jungle flex items-center justify-center mb-4">
                        <Leaf className="w-9 h-9 text-sand" />
                    </div>
                    <CardTitle className="font-heading text-2xl text-jungle">Nouveau mot de passe</CardTitle>
                    <CardDescription>Choisissez votre nouveau mot de passe</CardDescription>
                </CardHeader>
                <CardContent>
                    {done ? (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <p className="text-sm text-muted-foreground">Votre mot de passe a été modifié avec succès.</p>
                            <Link to="/connexion">
                                <Button className="bg-jungle hover:bg-jungle/90 text-sand">Se connecter</Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nouveau mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Minimum 6 caractères"
                                        required
                                        className="bg-white/80 border-jungle/20 rounded-xl"
                                        data-testid="new-password-input"
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Confirmer le mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Retapez le mot de passe"
                                        required
                                        className="bg-white/80 border-jungle/20 rounded-xl"
                                        data-testid="confirm-password-input"
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-testid="toggle-confirm-password">
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <Button type="submit" className="w-full bg-jungle hover:bg-jungle/90 text-sand rounded-xl h-11" disabled={loading} data-testid="reset-submit">
                                {loading ? 'Modification...' : 'Modifier le mot de passe'}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
